import type { ApiReturnResource } from '@highimpact-frontend/api-types';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { fetchDataWithParams } from '../asyncThunks';
import type { PermittedAction } from './data';
import type { DataStoreType, ExternalsWithParams } from './initials';
import { initials as initialData } from './initials';
import type { State } from './state';

export const getKeyFromParamsAndId = (
  params: object,
  id: ExternalsWithParams
) => {
  switch (id) {
    case 'campaigns':
      return `${(params as { site_id: number }).site_id}`;
  }
};

export const data_slice = createSlice({
  name: 'data-slice',

  initialState: initialData,

  reducers: {
    setExternalState(
      state,
      action: PayloadAction<{
        external_key: keyof DataStoreType['externals'];
        value: DataStoreType['externals'][keyof DataStoreType['externals']];
      }>
    ) {
      const { external_key, value } = action.payload;
      // Removes the WritableDraft generic type that causes an error TS2590
      type SimplifiedType = DataStoreType['externals'][typeof external_key];
      (state.externals[external_key] as SimplifiedType) = value;
    },

    setExternalStateWithParams(
      state,
      action: PayloadAction<{
        external_key: ExternalsWithParams;
        params: object;
        value: DataStoreType['externals'][ExternalsWithParams]['value'][string];
      }>
    ) {
      const { external_key, params, value } = action.payload;
      const key = getKeyFromParamsAndId(params, external_key);
      // Removes the WritableDraft generic type that causes an error TS2590
      type SimplifiedType = DataStoreType['externals'][typeof external_key];
      (state.externals[external_key] as SimplifiedType).value[key] = value;
    },

    setPermissionState(
      state,
      action: PayloadAction<{
        external_key: keyof DataStoreType['externals'];
        value: State<string, PermittedAction>;
      }>
    ) {
      const { external_key, value } = action.payload;
      // Removes the WritableDraft generic type that causes an error TS2590
      type SimplifiedType =
        DataStoreType['credentials']['permissions'][typeof external_key];
      (state.credentials.permissions[external_key] as SimplifiedType) = value;
    },

    setResources(
      state,
      action: PayloadAction<{
        values: { [KEY in number]: State<number, ApiReturnResource> };
      }>
    ) {
      const { values } = action.payload;
      Object.entries(values).forEach(([id, value]) => {
        state.resources[Number(id)] = value;
      });
    },

    resetExternalState(
      state,
      action: PayloadAction<{
        external_key: keyof DataStoreType['externals'];
      }>
    ) {
      const { external_key } = action.payload;
      // Removes the WritableDraft generic type that causes an error TS2590
      type SimplifiedType = DataStoreType['externals'][typeof external_key];
      (state.externals[external_key] as SimplifiedType) = {
        _tag: 'idle',
        type: 'externals',
        id: external_key,
      };
    },

    resetExternalStateWithParams(
      state,
      action: PayloadAction<{
        external_key: ExternalsWithParams;
        params: object;
      }>
    ) {
      const { external_key, params } = action.payload;
      const key = getKeyFromParamsAndId(params, external_key);
      // Removes the WritableDraft generic type that causes an error TS2590
      type SimplifiedType = DataStoreType['externals'][typeof external_key];
      (state.externals[external_key] as SimplifiedType).value[key] = {
        _tag: 'idle',
        type: 'externals',
        id: external_key,
      };
    },

    resetDataStore(state) {
      state.externals = initialData.externals;
      state.credentials = initialData.credentials;
      state.resources = initialData.resources;
      state.datasetsByParams = initialData.datasetsByParams;
    },

    resetSiteIdsToDashboardIds(state) {
      state.externals.siteIdsToDashboardIds =
        initialData.externals.siteIdsToDashboardIds;
    },

    resetCachedDataForUrlByPattern: (state, action: PayloadAction<string>) => {
      Object.keys(state.datasetsByParams)
        .filter((url) => url.startsWith(action.payload))
        .forEach(
          (url) =>
            (state.datasetsByParams[url] = {
              _tag: 'idle',
              type: 'cached',
              id: url,
            })
        );
    },

    resetCachedDataForUrl: (state, action: PayloadAction<string>) => {
      state.datasetsByParams[action.payload] = {
        _tag: 'idle',
        type: 'cached',
        id: action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDataWithParams.pending, (state, action) => {
        state.datasetsByParams[action.meta.arg] = {
          _tag: 'loading',
          type: state.datasetsByParams[action.meta.arg]?.type ?? 'cached',
          id: state.datasetsByParams[action.meta.arg]?.id ?? action.meta.arg,
        };
      })
      .addCase(fetchDataWithParams.fulfilled, (state, action) => {
        state.datasetsByParams[action.payload.url] = {
          _tag: 'data',
          data: action.payload.data,
          type: state.datasetsByParams[action.meta.arg]?.type ?? 'cached',
          id: state.datasetsByParams[action.meta.arg]?.id ?? action.meta.arg,
        };
      })
      .addCase(fetchDataWithParams.rejected, (state, action) => {
        state.datasetsByParams[action.meta.arg] = {
          _tag: 'error',
          error: action.payload ?? 'Unknown error',
          type: state.datasetsByParams[action.meta.arg]?.type ?? 'cached',
          id: state.datasetsByParams[action.meta.arg]?.id ?? action.meta.arg,
        };
      });
  },
});

export const {
  setExternalState,
  setExternalStateWithParams,
  resetExternalState,
  resetExternalStateWithParams,
  resetCachedDataForUrlByPattern,
  resetDataStore,
  setPermissionState,
  resetSiteIdsToDashboardIds,
  setResources,
  resetCachedDataForUrl,
} = data_slice.actions;

export default data_slice.reducer;
